import { ThemeDialog, subscribe, EVENTS } from '@theme/global';

/**
 * Product media gallery: syncs the viewer, dots and thumbnails; opens the zoom dialog;
 * jumps to the variant's featured media on variant change.
 */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.viewer = this.querySelector('[data-gallery-viewer]');
    this.slides = Array.from(this.querySelectorAll('.gallery__slide[data-media-id]'));
    this.dots = Array.from(this.querySelectorAll('.gallery__dot'));
    this.thumbs = Array.from(this.querySelectorAll('.gallery__thumb'));
    this.zoomDialog = this.querySelector('theme-dialog');
    this.layout = this.dataset.layout;

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.mediaId, true)));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    this.querySelectorAll('[data-zoom-trigger]').forEach((button) => button.addEventListener('click', (event) => this.openZoom(event.currentTarget.closest('.gallery__slide'))));

    if (this.viewer && 'IntersectionObserver' in window && this.layout !== 'stacked') {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(entry.target.dataset.mediaId);
          });
        },
        { root: this.viewer, threshold: [0.6] }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    this.unsubscribe = subscribe(EVENTS.variantChange, ({ sectionId, variant }) => {
      if (!this.closest('[data-section]') || this.closest('[data-section]').dataset.section !== sectionId) return;
      if (variant?.featured_media?.id) this.show(String(variant.featured_media.id), true);
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.unsubscribe?.();
  }

  get activeIndex() {
    return Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
  }

  step(direction) {
    const next = (this.activeIndex + direction + this.slides.length) % this.slides.length;
    this.show(this.slides[next].dataset.mediaId, true);
  }

  show(mediaId, scroll = false) {
    const slide = this.slides.find((s) => s.dataset.mediaId === mediaId);
    if (!slide) return;
    this.setActive(mediaId);
    if (!scroll) return;
    if (this.layout === 'stacked' && window.matchMedia('(min-width: 1024px)').matches) {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    this.viewer.scrollTo({ left: slide.offsetLeft - this.viewer.offsetLeft, behavior: 'smooth' });
  }

  setActive(mediaId) {
    this.slides.forEach((slide) => {
      const active = slide.dataset.mediaId === mediaId;
      slide.classList.toggle('is-active', active);
      if (active) slide.setAttribute('aria-current', 'true');
      else slide.removeAttribute('aria-current');
      if (!active) slide.querySelector('deferred-media')?.pause?.();
    });
    this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaId === mediaId));
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.mediaId === mediaId;
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else thumb.removeAttribute('aria-current');
    });
  }

  openZoom(slide) {
    if (!this.zoomDialog || !slide) return;
    const viewer = this.zoomDialog.querySelector('[data-zoom-viewer]');
    const img = slide.querySelector('img');
    if (!img) return;
    const src = img.currentSrc || img.src;
    const large = src.replace(/(&|\?)width=\d+/, '$1width=2400');
    viewer.innerHTML = `<img src="${large}" alt="${img.alt || ''}">`;
    viewer.classList.remove('is-zoomed');
    viewer.onclick = () => viewer.classList.toggle('is-zoomed');
    this.zoomDialog.open(slide.querySelector('[data-zoom-trigger]'));
  }
}
if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

/** Loads video / model markup from a <template> on first interaction. */
class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this.poster = this.querySelector('.deferred-media__poster');
    this.poster?.addEventListener('click', () => this.load());
  }
  load() {
    if (this.hasAttribute('loaded')) return;
    const template = this.querySelector('template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    this.append(content);
    this.setAttribute('loaded', '');
    const video = this.querySelector('video');
    video?.play?.().catch(() => {});
    if (this.querySelector('model-viewer') && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }]);
    }
  }
  pause() {
    this.querySelector('video')?.pause?.();
  }
}
if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);
